/**
 * استان و شهر — the pickable list, and the matching that makes it safe.
 *
 * The names themselves live in `iranGeo.json` beside this file. They are DATA,
 * not UI copy: the exact string is stored on the clinic record, it is the same
 * word in an English UI, and translating it would orphan every clinic already
 * saved with a Persian province. JSON also keeps them out of the reach of
 * `scripts/i18n-audit.js`, which walks `.js`/`.jsx` only.
 *
 * ── The rule this module exists to enforce ───────────────────────────────────
 *
 * These two fields were free text until now, and `updateClinic` is a FULL
 * REPLACE (see `Clinic.shared.js` → `toUpdateInput`). So a `<select>` whose
 * options do not contain what the clinic already has renders EMPTY, and the
 * very next save writes that emptiness over a real province. Every caller must
 * therefore go through `provinceOptions` / `cityOptions`, which carry the stored
 * value into the list whenever it is not one of ours.
 *
 * ── Matching ────────────────────────────────────────────────────────────────
 *
 * A geocoder does not answer in our spelling. Nominatim returns «استان تهران»
 * where this list says «تهران», and Persian text arrives with Arabic ي/ك, with
 * or without ZWNJ, and occasionally with Persian digits. `matchProvince` and
 * `matchCity` fold all of that away and give back OUR canonical spelling, so a
 * clinic located from the map and a clinic picked from the list store the same
 * string.
 */

import geo from './iranGeo.json';
import geoEn from './iranGeoEn.json';

const EN_NAMES = geoEn.names;

/** Sentinel option value meaning «somewhere not in the list» — free text. */
export const OTHER = '__other__';

const PROVINCES = geo.provinces;

/** Canonical province names, in the order the picker shows them. */
export const PROVINCE_NAMES = PROVINCES.map((p) => p.name);

/**
 * A place as an English desk reads it. The VALUE stays the Persian name — that is
 * what every saved clinic holds (see the header) — only the label follows the
 * language. Arabic keeps the Persian name, which it reads as written.
 */
export function placeLabel(name, locale) {
  return (locale === 'en' && EN_NAMES[name]) || name;
}

const BY_PROVINCE = new Map(PROVINCES.map((p) => [p.name, p.cities]));

// i18n-exempt: administrative prefixes stripped before matching — data, not UI copy.
const PREFIXES = ['استان ', 'شهرستان ', 'شهر ', 'بخش '];

const DIGITS = { '۰': '0', '۱': '1', '۲': '2', '۳': '3', '۴': '4', '۵': '5', '۶': '6', '۷': '7', '۸': '8', '۹': '9', '٠': '0', '١': '1', '٢': '2', '٣': '3', '٤': '4', '٥': '5', '٦': '6', '٧': '7', '٨': '8', '٩': '9' };  // i18n-exempt: digit glyphs folded for comparison — data, not UI copy.

/**
 * A place name reduced to something two spellings of the same place agree on.
 *
 * Comparison only — never store or display the result. Spacing is folded away
 * entirely, ZWNJ included, because «خمینی‌شهر», «خمینی شهر» and «خمینیشهر» are
 * one city typed by three people and the picker has to treat them as one.
 */
export function normalizePlace(value) {
  if (value == null) return '';
  let out = String(value).trim();
  if (!out) return '';
  out = out
    .replace(/[\u064a\u0649]/g, '\u06cc')                      // Arabic ي / ى → Persian ی
    .replace(/\u0643/g, '\u06a9')                              // Arabic ك → Persian ک
    .replace(/[\u200c\u00a0]/g, ' ')                           // ZWNJ / nbsp → a plain space
    .replace(/[\u200b\u200d-\u200f\u202a-\u202e\u064b-\u0652]/g, '')  // bidi marks, harakat
    .replace(/[\u06f0-\u06f9\u0660-\u0669]/g, (d) => DIGITS[d] || d)
    .replace(/\s+/g, ' ')
    .trim();
  for (const prefix of PREFIXES) {
    if (out.startsWith(prefix)) { out = out.slice(prefix.length).trim(); break; }
  }
  // Whitespace goes LAST and goes entirely: «خمینی‌شهر», «خمینی شهر» and
  // «خمینیشهر» are one city typed three ways, and the key has to agree. The
  // prefix strip above needs the spaces, which is why this is not done sooner.
  return out.replace(/\s+/g, '');
}

const PROVINCE_INDEX = new Map(PROVINCE_NAMES.map((name) => [normalizePlace(name), name]));

/** The cities of a province, or `[]` for a province this list does not know. */
export function citiesOf(province) {
  const canonical = matchProvince(province);
  return (canonical && BY_PROVINCE.get(canonical)) || [];
}

/** Our spelling of a province, or `null` when it is not one of ours. */
export function matchProvince(raw) {
  const key = normalizePlace(raw);
  if (!key) return null;
  return PROVINCE_INDEX.get(key) || null;
}

/**
 * Our spelling of a city.
 *
 * Searched inside the province when one is given — «نورآباد» exists in both
 * فارس and لرستان — and across every province only as a fallback, so a geocoder
 * that named the city but not the province still lands on something real.
 */
export function matchCity(province, raw) {
  const key = normalizePlace(raw);
  if (!key) return null;
  const within = citiesOf(province).find((c) => normalizePlace(c) === key);
  if (within) return within;
  if (matchProvince(province)) return null;
  for (const p of PROVINCES) {
    const hit = p.cities.find((c) => normalizePlace(c) === key);
    if (hit) return hit;
  }
  return null;
}

/** The province a city belongs to, or `null` — used to fill in a missing province. */
export function provinceOfCity(raw) {
  const key = normalizePlace(raw);
  if (!key) return null;
  const owner = PROVINCES.find((p) => p.cities.some((c) => normalizePlace(c) === key));
  return owner ? owner.name : null;
}

/**
 * `{value, label}` options for the province picker.
 *
 * `stored` is what the clinic already has. When it is not one of ours it is
 * prepended verbatim — that is the whole point: the field must be able to
 * render, and re-save, a value this list has never heard of.
 */
export function provinceOptions(stored, locale) {
  const known = PROVINCE_NAMES.map((name) => ({ value: name, label: placeLabel(name, locale) }));
  const value = String(stored || '').trim();
  if (value && !matchProvince(value)) return [{ value, label: value }, ...known];
  return known;
}

/** `{value, label}` options for the city picker, same contract as above. */
export function cityOptions(province, stored, locale) {
  const known = citiesOf(province).map((name) => ({ value: name, label: placeLabel(name, locale) }));
  const value = String(stored || '').trim();
  if (value && !known.some((o) => normalizePlace(o.value) === normalizePlace(value))) {
    return [{ value, label: value }, ...known];
  }
  return known;
}
